import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

/**
 * Plegar sin que la página salte bajo el mouse.
 *
 * La banda es lo ÚLTIMO de la página: plegar las ~50 tarjetas de Dashbo achica el documento por
 * debajo del scroll actual, el navegador recorta el `scrollTop` al nuevo máximo y todo lo de arriba
 * baja de golpe. Medido en prod (QA-banda-05, 26/09): con el rótulo en y = 120, plegar lo dejaba en
 * y = 664 — 544 px bajo el mouse, que quedaba apuntando a otra cosa.
 *
 * El arreglo es RESERVAR el alto que faltó: después del render que plegó se mide cuánto se corrió el
 * ancla (el rótulo), se agrega un hueco de ese alto al final y se devuelve el scroll — el rótulo
 * queda exactamente donde estaba. El hueco se va solo cuando sale de la vista por abajo: ahí
 * sacarlo ya no recorta nada. Todo pasa en layout effects, antes de pintar: no hay parpadeo.
 */

/** El contenedor que scrollea de verdad: en la 2.1 es `.app-content`, no la ventana. */
export function scrollParentOf(el) {
    let node = el ? el.parentElement : null;
    while (node) {
        const {overflowY} = window.getComputedStyle(node);
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
            return node;
        }
        node = node.parentElement;
    }
    return document.scrollingElement || document.documentElement;
}

export function useStableCollapse() {
    const anchorRef = useRef(null);
    const reserveRef = useRef(null);
    const topBefore = useRef(null);
    const [reserve, setReserve] = useState(0);

    /** Se llama justo ANTES de plegar: dónde está el ancla en la pantalla. */
    const markBeforeCollapse = useCallback(() => {
        topBefore.current = anchorRef.current ? anchorRef.current.getBoundingClientRect().top : null;
    }, []);

    /** Al desplegar el hueco sobra: vuelve el contenido que lo justificaba. */
    const clearReserve = useCallback(() => setReserve(0), []);

    // 1) Después del render que plegó: cuánto bajó el ancla porque el navegador recortó el scroll.
    useLayoutEffect(() => {
        if (topBefore.current === null || !anchorRef.current) return;
        const drift = anchorRef.current.getBoundingClientRect().top - topBefore.current;
        topBefore.current = null;
        if (drift > 1) setReserve(drift);
    });

    // 2) Con el hueco ya en el DOM, se devuelve el scroll: el ancla vuelve adonde estaba.
    useLayoutEffect(() => {
        if (!reserve || !anchorRef.current) return;
        scrollParentOf(anchorRef.current).scrollTop += reserve;
    }, [reserve]);

    // 3) El hueco se va cuando queda entero debajo de lo visible. `capture` porque el scroll de un
    //    contenedor interno (`.app-content`) no burbujea hasta el documento.
    useEffect(() => {
        if (!reserve) return undefined;
        const onScroll = () => {
            const el = reserveRef.current;
            if (el && el.getBoundingClientRect().top >= window.innerHeight) setReserve(0);
        };
        document.addEventListener('scroll', onScroll, true);
        return () => document.removeEventListener('scroll', onScroll, true);
    }, [reserve]);

    return {anchorRef, reserveRef, reserve, markBeforeCollapse, clearReserve};
}

export default useStableCollapse;
